import type { EventTypeAttachment, SearchEndpointResponse } from '@ps/isp-web-services-types';
import type { Alias } from '@ps/web-core-store';

import { initialSearchResults } from '../model/defaultState';
import type { SearchLinks, SearchMatches, SearchResults } from '../model/types';

export function getLinksMatchingQuery(searchQuery: string, links: SearchLinks) {
  return links.filter((link) => {
    if ('text' in link) {
      return link.text.toLowerCase().includes(searchQuery);
    }

    if ('runnerName' in link) {
      return link.runnerName.toLowerCase().includes(searchQuery);
    }

    if ('venue' in link) {
      return link.venue.toLowerCase().includes(searchQuery);
    }

    return false;
  });
}

export function mapLinkToSearchMatch(link: SearchLinks[number]): SearchMatches {
  const isLinkEventOrCompetition = 'type' in link;
  let alias: Alias = 'sportsAnimalRacingRace';

  if (isLinkEventOrCompetition) {
    alias = link.type === 'EVENT' ? 'sportsEvent' : 'sportsCompetition';
  }

  return {
    alias,
    link
  };
}

export function getUniqueEventTypes(
  links: SearchLinks,
  eventTypes: Partial<SearchEndpointResponse['eventTypes']>
) {
  const seenEventTypeIds = new Set();
  const uniqueEventTypes: EventTypeAttachment[] = [];

  for (const { eventTypeId } of links) {
    if (!seenEventTypeIds.has(eventTypeId)) {
      seenEventTypeIds.add(eventTypeId);
      const eventType = eventTypes[eventTypeId];
      if (eventType) {
        uniqueEventTypes.push(eventType);
      }
    }
  }
  return uniqueEventTypes;
}

export const getSearchResults = (
  searchQuery: string,
  { eventsAndRunners, competitionsAndMeetings, eventTypes }: SearchEndpointResponse
): SearchResults => {
  const combinedLinks: SearchLinks = [...eventsAndRunners, ...competitionsAndMeetings];

  const linksByQuery = getLinksMatchingQuery(searchQuery, combinedLinks);

  if (!linksByQuery.length) {
    return initialSearchResults;
  }

  const linksUniqueEventTypes = getUniqueEventTypes(linksByQuery, eventTypes);
  const linksToSearchMatches = linksByQuery.map(mapLinkToSearchMatch);

  return {
    uniqueEventTypes: linksUniqueEventTypes,
    matches: linksToSearchMatches
  };
};
